// RITA-1543 — Verkleinerung der A4-Seiten in der Vorschau, passend zur verfügbaren Breite der Spalte.
//
// Eine feste Verkleinerung schnitt die Seite bei mittleren und schmalen Fenstern rechts ab. Gemessen wird
// der Rahmen um die Seiten (Muster wie layout/useLeistenposition: vor dem ersten Zeichnen, danach per
// ResizeObserver, Schreiben erst im nächsten Frame). Die Verkleinerung wirkt nur auf die angezeigten
// Seiten, nicht auf den Rahmen und nicht auf die Seitenmessung – sie kann sich also nicht selbst antreiben.
import { RefObject, useLayoutEffect, useRef, useState } from 'react';

export const VORSCHAU_ZOOM_MAX = 0.62;
export const VORSCHAU_ZOOM_MIN = 0.2;

/** Größte Verkleinerung, bei der die ganze Seitenbreite in den Rahmen passt (auf Hundertstel abgerundet). */
export function einpassZoom(verfuegbarPx: number, seitenbreitePx: number): number {
  if (!(verfuegbarPx > 0) || !(seitenbreitePx > 0)) return VORSCHAU_ZOOM_MAX;
  const zoom = Math.floor((verfuegbarPx / seitenbreitePx) * 100) / 100;
  return Math.min(VORSCHAU_ZOOM_MAX, Math.max(VORSCHAU_ZOOM_MIN, zoom));
}

export default function useEinpassZoom(seitenbreitePx: number): {
  rahmenRef: RefObject<HTMLDivElement>;
  zoom: number;
} {
  const rahmenRef = useRef<HTMLDivElement>(null);
  const [breite, setBreite] = useState(0);

  useLayoutEffect(() => {
    const rahmen = rahmenRef.current;
    if (!rahmen) return undefined;
    let frame = 0;
    const messen = () => {
      frame = 0;
      const neu = rahmen.clientWidth;
      setBreite((alt) => (alt === neu ? alt : neu));
    };
    const planen = () => {
      if (!frame) frame = window.requestAnimationFrame(messen);
    };
    messen();
    const beobachter = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(planen);
    beobachter?.observe(rahmen);
    window.addEventListener('resize', planen);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      beobachter?.disconnect();
      window.removeEventListener('resize', planen);
    };
  }, []);

  return { rahmenRef, zoom: einpassZoom(breite, seitenbreitePx) };
}
